/**
 * How many decimal places this tick size actually needs, exactly.
 *
 * NOT `ceil(-log10(tick))`. That approximation is right only for powers of ten and silently
 * under-reports every other tick: it gives 3 for a 0.0025 tick that plainly needs 4, and 1 for a
 * 0.25 tick that needs 2. NSE currency derivatives all quote on a 0.0025 tick -- 6,872 instruments
 * -- so the approximation was rendering every one of them a decimal short of a single tick.
 *
 * Counts the digits instead. Handles exponent notation, because a 1e-7 tick stringifies as
 * "1e-7" and a naive indexOf('.') finds nothing.
 *
 * Returns 0 for a tick of 1 or larger, and null when there is no usable tick.
 */
export function tickDecimals(tickRaw: unknown): number | null {
  const t = tickRaw == null || tickRaw === '' ? NaN : Number(tickRaw);
  if (!Number.isFinite(t) || t <= 0) return null;
  if (t >= 1) return 0;
  // toFixed(12) is well inside double precision for any real tick and avoids the exponent form.
  // Trailing zeros are stripped so 0.0025 -> '0.0025' -> 4, and 1e-7 -> '0.0000001' -> 7.
  const fixed = t.toFixed(12).replace(/0+$/, '');
  const dot = fixed.indexOf('.');
  if (dot < 0) return 0;
  return Math.max(0, Math.min(8, fixed.length - dot - 1));
}

/**
 * Resolve the display decimal precision for an instrument.
 *
 * THE TICK IS THE AUTHORITY. It is the venue's own statement of the smallest increment a price
 * can move by, so it decides how many decimals a price has -- both the ceiling AND the floor.
 * `decimalPrecision` is a fallback for instruments that carry no tick, nothing more.
 *
 * WHY THIS CHANGED (2026-09-05)
 * The rule used to be `min(storedPrecision, tickPrecision)` -- a ceiling only. That fixed the
 * loud half of the problem (the DELTA import writes a blanket `decimalPrecision: 8`, so BTCUSD on
 * a 0.5 tick rendered `80000.00000000`) and left the quiet half wide open: a stored value COARSER
 * than the tick was trusted without question. AKEUSD quotes on a 1e-7 tick and stores 2, so its
 * price axis, its LTP badge and its crosshair all read `0.02` while the instrument traded at
 * 0.0162400 -- the entire price collapsed onto two gridlines and no level could be read off the
 * chart at all. 146 of 184 sampled OHLC values from that symbol carry seven decimals, so the tick
 * is real and it was the display that was wrong.
 *
 * The same file already took `max(decimalPrecision, tickPrecision)` when snapping a dragged line
 * to a tick, which is why a clicked level landed correctly while the axis behind it did not. One
 * rule now, and the tick wins.
 *
 * THIS SUPERSEDES R78 AT DISPLAY TIME, DELIBERATELY.
 * `decimalPrecision` is user-editable (Instruments -> bulk edit) and the DELTA import is careful
 * to cap it and never raise it, so a hand-set value survives a re-import. That protection is
 * about what the IMPORT writes; this is about what the SCREEN renders, and the two are different
 * questions. A hand-set value finer than the tick is still honoured downward by the tick, and a
 * hand-set value COARSER than the tick is now overridden -- because 2 decimals on a 1e-7
 * instrument is not a preference anyone can act on, it is an unreadable price. If a coarser
 * display is ever genuinely wanted on a high-priced instrument (BTCUSD 79586.5 -> 79586), that
 * needs its own field; it cannot share one with the value the import maintains.
 *
 * Accepts a few shapes the API returns:
 *   - { decimalPrecision, tickSize, exchange }   <- Instrument
 *   - { instrument: { decimalPrecision, tickSize, exchange } }  <- Trade / Alert
 *
 * Returns an integer in [0, 8].
 */
export function resolveDecimals(input: any): number {
  if (input == null) return 2;
  const inst = input.instrument ?? input;

  const fromTick = tickDecimals(inst?.tickSize ?? input?.tickSize);
  if (fromTick != null) return fromTick;

  const dpRaw = inst?.decimalPrecision ?? input?.decimalPrecision;
  const dp = dpRaw != null && dpRaw !== '' ? Number(dpRaw) : null;
  if (dp != null && Number.isFinite(dp)) return Math.max(0, Math.min(8, Math.floor(dp)));

  const exch = String(inst?.exchange ?? input?.exchange ?? '').toUpperCase();
  if (exch === 'MCX') return 0;
  if (exch === 'CDS') return 4;
  return 2;
}

/**
 * Display precision from the VALUE ALONE, for surfaces that have no instrument row.
 *
 * The Charts symbol pane is one: its rows are `{symbol, exchange}` plus whatever meta the
 * filter carried, and no tick size or decimalPrecision travels with them. Calling
 * `resolveDecimalsFor(null, v)` there does not work — with no input it takes base = 2 and
 * then caps the magnitude fallback at that base, so an alert on a token quoted at 0.00774
 * rendered as "0.01 → 0.01 (-1.83%)": two prices that look identical next to a move that
 * says they are not.
 *
 * Same spirit as the magnitude fallback in resolveDecimalsFor, but without a base to clamp
 * against. Prefer resolveDecimals / resolveDecimalsFor wherever an instrument IS in scope —
 * the tick is always the better answer than the number's size.
 */
export function decimalsForValue(value: number | null | undefined): number {
  const v = value == null ? null : Number(value);
  if (v == null || !Number.isFinite(v)) return 2;
  const a = Math.abs(v);
  if (a >= 1) return 2;
  if (a >= 0.01) return 4;
  if (a >= 0.0001) return 6;
  return 8;
}

/** True when the input carries a tick size we can derive precision from. */
export function hasUsableTick(input: any): boolean {
  if (input == null) return false;
  const inst = input.instrument ?? input;
  const raw = inst?.tickSize ?? input?.tickSize;
  const t = raw != null && raw !== '' ? Number(raw) : 0;
  return t > 0 && Number.isFinite(t);
}

/**
 * Display precision for ONE value, for payloads that carry `decimalPrecision`
 * but no `tickSize`.
 *
 * `/v1/dashboard/summary` is such a payload: its instrument projection includes
 * the (wrong) stored precision of 8 and omits the tick, so `resolveDecimals`
 * has nothing to cap against and faithfully returns 8 — which is how the
 * Dashboard printed `870.00000000` and `102.49969395` for a 0.05-tick
 * instrument. When there IS a tick this defers to it entirely; only when there
 * is none does it fall back on magnitude, because a price at or above 1 has
 * never meaningfully needed eight decimals. Sub-1 prices keep their precision —
 * that is where small-tick instruments actually live.
 *
 * The payload gap is the real defect — see docs/CHARTS-FUNCTIONAL-ISSUES.md.
 */
export function resolveDecimalsFor(input: any, value?: number | null): number {
  const base = resolveDecimals(input);
  if (hasUsableTick(input)) return base;
  const v = value == null ? null : Number(value);
  if (v == null || !Number.isFinite(v)) return base;
  const a = Math.abs(v);
  if (a >= 1) return Math.min(base, 2);
  /* Sub-1 prices genuinely need decimals, but not eight: the stored 8 is the
     import's blanket value, not a real requirement. Six covers every sub-1
     instrument in the tracked set; below 0.01 the stored value stands. */
  if (a >= 0.01) return Math.min(base, 6);
  return base;
}
